/**
 * adultAutismFirestorePaths.js
 *
 * Thin wrappers around the module-parameterised path builders in
 * modularFirestorePaths.js, pre-bound to the 'adultAutism' module.
 *
 * Existing consumers import these named exports without change — the function
 * signatures and return values are identical to the original hardcoded versions.
 *
 * New code should prefer importing from modularFirestorePaths.js directly and
 * passing the assessmentModule explicitly, so it works for any pathway.
 */

import {
  getAssessmentDocPathSegments,
  getFormInstancePathSegments,
  getAssessmentStagePathSegments,
  getReportContentPathSegments,
  getPatientHeaderPathSegments as _getPatientHeaderPathSegments,
  joinFirestorePath as _joinFirestorePath,
} from './modularFirestorePaths';

const AUTISM_COLLECTION = 'adult_autism';

export const AUTISM_ASSESSMENT_DOC_ID = 'assessment_001';

/** @returns {string[]} */
export function getPatientHeaderPathSegments(patientUid) {
  return _getPatientHeaderPathSegments(patientUid);
}

/** @returns {string[]} */
export function getPatientAssessmentDocPathSegments(patientUid) {
  return getAssessmentDocPathSegments(patientUid, AUTISM_COLLECTION, AUTISM_ASSESSMENT_DOC_ID);
}

/** @returns {string[]} */
export function getPatientFormInstancePathSegments(patientUid, formKey) {
  return getFormInstancePathSegments(patientUid, AUTISM_COLLECTION, AUTISM_ASSESSMENT_DOC_ID, formKey);
}

/** @returns {string[]} */
export function getPatientAssessmentStagePathSegments(patientUid, stageKey) {
  return getAssessmentStagePathSegments(patientUid, AUTISM_COLLECTION, AUTISM_ASSESSMENT_DOC_ID, stageKey);
}

/**
 * Firestore path segments for a reportContent document.
 * Path: patients/{uid}/adult_autism/assessment_001/reportContent/{stageKey}
 *
 * @param {string} patientUid
 * @param {string} stageKey - e.g. 'history', 'semiStructuredInterview'
 * @returns {string[]}
 */
export function getPatientReportContentPathSegments(patientUid, stageKey) {
  return getReportContentPathSegments(patientUid, AUTISM_COLLECTION, AUTISM_ASSESSMENT_DOC_ID, stageKey);
}

/**
 * Join path segments into a slash-separated string for assertions in tests.
 * @param {string[]} segments
 * @returns {string}
 */
export function joinFirestorePath(segments) {
  return _joinFirestorePath(segments);
}
